/**
 * Client-side alcohol detection for diary entries — port of mobile
 * utils/alcohol.ts (keep in sync). The authoritative detection lives in the
 * scoring pipeline; this mirror is for display decisions only:
 *  - excluding alcoholic "fluids" from the hydration goal,
 *  - celebrating alcohol-free days in insights,
 *  - the day score's alcohol override (diary/today-score.ts).
 * Never used for lecturing — tone stays compassionate everywhere.
 */

// Name matching is shared byte-identical with the server override and the
// mobile app (./alcoholPatterns.ts — same patterns and alcohol-free /
// cooked-dish exclusions).
import { nameLooksAlcoholic } from './alcoholPatterns'

/** Structural subset of DiaryEntry needed for detection. */
interface AlcoholCheckable {
  name?: string
  foods?: { name?: string }[]
  analysis?: { scoreBreakdown?: unknown; scoreContext?: unknown }
}

/**
 * True when a diary entry contains alcohol (scored driver or name match).
 *
 * 1.5.0 (mobile parity): an entry whose analysis carries a score context is
 * re-scored on every food edit, so its driver already says whether alcohol is
 * still in it. Its foods are still name-checked, but its NAME is not —
 * "Burger and beer" keeps that name after the beer is removed.
 */
export function entryHasAlcohol(entry: AlcoholCheckable): boolean {
  if ((entry.analysis?.scoreBreakdown as { driver?: string } | undefined)?.driver === 'alcohol') return true
  const foods = entry.foods || []
  if (entry.analysis?.scoreContext != null && foods.length > 0) {
    return foods.some((f) => nameLooksAlcoholic(f.name))
  }
  if (nameLooksAlcoholic(entry.name)) return true
  return foods.some((f) => nameLooksAlcoholic(f.name))
}

/**
 * Nutritionally meaningful fluid? Port of mobile utils/fluids.ts —
 * fluids with real nutrients render in a meal section as well as the fluid
 * tracker; trace amounts (black coffee, tea) stay tracker-only.
 */
export function fluidEntryHasNutrients(entry: {
  nutrients?: {
    calories?: number
    addedSugarsG?: number
    carbsG?: number
    saturatedFatG?: number
    sodiumMg?: number
    proteinG?: number
  }
}): boolean {
  const n = entry.nutrients
  if (!n) return false
  return (
    (n.calories ?? 0) >= 10 ||
    (n.addedSugarsG ?? 0) >= 1 ||
    (n.carbsG ?? 0) >= 1 ||
    (n.saturatedFatG ?? 0) >= 0.5 ||
    (n.sodiumMg ?? 0) >= 50 ||
    (n.proteinG ?? 0) >= 1
  )
}
